/**
 * Authentication Redux Thunks
 * 
 * Async actions for authentication operations using Firebase.
 * Handles login, registration, logout, and profile management.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signOutUser,
  resetPassword,
  changePassword,
  updateUserProfile,
  getCurrentUser,
  reauthenticateUser,
} from "./authService";
import { saveUserData, getUserData } from "./userService";
import { buildAppUser } from "./userModel";
import { getWriterPlanFeatures } from "../config/writerPlans";
import { formatFirebaseAuthError, logError } from "../utils/errorHandler";
import { pushActivity, ACTIVITY_TYPES } from "../services/activityLogService";

async function tokenClaimsFor(firebaseUser) {
  try {
    const tokenResult = await firebaseUser.getIdTokenResult();
    return tokenResult?.claims || {};
  } catch {
    return {};
  }
}

async function buildSessionUser(firebaseUser, dbUserData) {
  const tokenClaims = await tokenClaimsFor(firebaseUser);
  return buildAppUser({ firebaseUser, dbUserData, tokenClaims });
}

/**
 * Login with email and password
 */
export const loginThunk = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const { email, password } = credentials;
      const userCredential = await signInWithEmail(email, password);
      const firebaseUser = userCredential.user;
      
      // Get additional user data from Firebase Realtime Database
      const dbUserData = await getUserData(firebaseUser.uid);
      return buildSessionUser(firebaseUser, dbUserData);
    } catch (error) {
      logError(error, "Login");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Register new user with email and password
 */
export const registerThunk = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const { email, password, fullName, phoneNumber, gender, role, authorName } = userData;
      
      // Create Firebase account
      const userCredential = await signUpWithEmail(email, password);
      const firebaseUser = userCredential.user;
      
      // Update profile with display name
      if (fullName) {
        await updateUserProfile({ displayName: fullName });
      }
      
      // Save additional user data to Firebase Realtime Database
      const normalizedRole = (role || "reader").toLowerCase();
      const roles = normalizedRole === "writer" ? ["reader", "writer"] : ["reader"];
      const subscriptions = {
        reader: true,
        writer: normalizedRole === "writer",
        writerPlan: "free",
      };
      const userProfileData = {
        phoneNumber,
        gender,
        role: normalizedRole, // legacy
        roles,
        subscriptions,
        currentMode: normalizedRole,
        fullName,
        email,
        authorName: authorName || "", // Save authorName for writers
      };
      await saveUserData(firebaseUser.uid, userProfileData);

      pushActivity({
        type: ACTIVITY_TYPES.USER_CREATED,
        userId: firebaseUser.uid,
        userEmail: firebaseUser.email,
        userName: fullName || firebaseUser.displayName,
        role: normalizedRole,
      }).catch(() => {});

      const dbUserData = await getUserData(firebaseUser.uid);
      return buildSessionUser(firebaseUser, dbUserData);
    } catch (error) {
      logError(error, "Registration");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Login with Google
 */
export const loginWithGoogleThunk = createAsyncThunk(
  "auth/loginWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const userCredential = await signInWithGoogle();
      const firebaseUser = userCredential.user;
      
      // Check if user exists in database to get their role
      const dbUserData = await getUserData(firebaseUser.uid);
      
      // If user doesn't exist, save basic info
      if (!dbUserData) {
        await saveUserData(firebaseUser.uid, {
          fullName: firebaseUser.displayName || "",
          email: firebaseUser.email,
          role: "reader", // legacy
          roles: ["reader"],
          subscriptions: { reader: true, writer: false },
          currentMode: "reader",
        });
        pushActivity({
          type: ACTIVITY_TYPES.USER_CREATED,
          userId: firebaseUser.uid,
          userEmail: firebaseUser.email,
          userName: firebaseUser.displayName || "",
          role: "reader",
        }).catch(() => {});
      }

      const freshDbUserData = dbUserData || (await getUserData(firebaseUser.uid));
      return buildSessionUser(firebaseUser, freshDbUserData);
    } catch (error) {
      logError(error, "Google Login");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Get current authenticated user
 */
export const getCurrentUserThunk = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const firebaseUser = getCurrentUser();
      if (!firebaseUser) {
        return rejectWithValue("No user is currently signed in");
      }
      
      // Get additional user data from Firebase Realtime Database
      const dbUserData = await getUserData(firebaseUser.uid);
      if (dbUserData) {
        const { syncPublicProfile } = await import("../services/publicProfileService");
        syncPublicProfile(firebaseUser.uid, {
          ...dbUserData,
          displayName: dbUserData.displayName || firebaseUser.displayName || "",
          photoURL: dbUserData.photoURL || firebaseUser.photoURL || "",
        }).catch(() => {});
      }
      return buildSessionUser(firebaseUser, dbUserData);
    } catch (error) {
      logError(error, "Get Current User");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Update user profile
 */
export const updateProfileThunk = createAsyncThunk(
  "auth/updateProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const user = getCurrentUser();
      if (!user) {
        return rejectWithValue("No user is currently signed in");
      }

      // Update Firebase Auth profile (displayName, photoURL)
      const authUpdates = {};
      if (profileData.displayName) {
        authUpdates.displayName = profileData.displayName;
      }
      if (profileData.photoURL) {
        authUpdates.photoURL = profileData.photoURL;
      }
      
      if (Object.keys(authUpdates).length > 0) {
        await updateUserProfile(authUpdates);
      }

      // Update Firebase Realtime Database (phoneNumber, gender, fullName, etc.)
      const { updateUserData } = await import("./userService");
      const currentDbUserData = await getUserData(user.uid);
      const normalizedRole = String(currentDbUserData?.role || "").toLowerCase();
      const canCustomizeSidebarLogo = normalizedRole === "writer" || normalizedRole === "superadmin";
      const planFeatures = getWriterPlanFeatures(currentDbUserData);
      const dbRoleLower = String(currentDbUserData?.role || "").toLowerCase();
      const canWriterBranding = dbRoleLower === "superadmin" || planFeatures.allowBranding;
      const dbUpdates = {};
      if (profileData.displayName !== undefined) {
        dbUpdates.displayName = profileData.displayName;
      }
      if (profileData.photoURL !== undefined) {
        dbUpdates.photoURL = profileData.photoURL;
      }
      if (profileData.phoneNumber !== undefined) {
        dbUpdates.phoneNumber = profileData.phoneNumber;
      }
      if (profileData.gender !== undefined) {
        dbUpdates.gender = profileData.gender;
      }
      if (profileData.fullName !== undefined) {
        dbUpdates.fullName = profileData.fullName;
      }
      if (profileData.sidebarLogoUrl !== undefined && canCustomizeSidebarLogo && canWriterBranding) {
        dbUpdates.sidebarLogoUrl = profileData.sidebarLogoUrl;
      }
      if (profileData.writerTheme !== undefined && canCustomizeSidebarLogo && planFeatures.allowWriterTheme) {
        dbUpdates.writerTheme = profileData.writerTheme;
      }

      if (Object.keys(dbUpdates).length > 0) {
        await updateUserData(user.uid, dbUpdates);
      }
      
      // Get updated user data
      const updatedUser = getCurrentUser();
      const dbUserData = await getUserData(user.uid);

      pushActivity({
        type: ACTIVITY_TYPES.USER_UPDATED,
        userId: user.uid,
        userEmail: user.email,
        userName: updatedUser.displayName || profileData.fullName,
      }).catch(() => {});

      return {
        displayName: updatedUser.displayName,
        photoURL: updatedUser.photoURL,
        phoneNumber: dbUserData?.phoneNumber || profileData.phoneNumber || "",
        gender: dbUserData?.gender || profileData.gender || "",
        sidebarLogoUrl: dbUserData?.sidebarLogoUrl || "",
        writerTheme: dbUserData?.writerTheme || null,
        subscriptionBilling: dbUserData?.subscriptionBilling || null,
        ...profileData,
      };
    } catch (error) {
      logError(error, "Update Profile");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Logout user
 */
export const logoutThunk = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await signOutUser();
      return null;
    } catch (error) {
      logError(error, "Logout");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Send password reset email
 */
export const resetPasswordThunk = createAsyncThunk(
  "auth/resetPassword",
  async (email, { rejectWithValue }) => {
    try {
      await resetPassword(email);
      return { success: true };
    } catch (error) {
      logError(error, "Reset Password");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Change user password
 */
export const changePasswordThunk = createAsyncThunk(
  "auth/changePassword",
  async ({ newPassword, email, currentPassword }, { rejectWithValue }) => {
    try {
      // Reauthenticate if current password is provided
      if (email && currentPassword) {
        await reauthenticateUser(email, currentPassword);
      }
      
      await changePassword(newPassword);
      return { success: true };
    } catch (error) {
      logError(error, "Change Password");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Delete user account
 */
export const deleteUserThunk = createAsyncThunk(
  "auth/deleteUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { deleteUserAccount } = await import("./authService");
      const { purgeUserAccountData } = await import("./accountDeletionService");

      const firebaseUser = getCurrentUser();
      if (!firebaseUser) {
        return rejectWithValue("You must be signed in to delete your account.");
      }

      // Reauthenticate before deleting (required for email/password; Google may need recent login)
      if (email && password) {
        await reauthenticateUser(email, password);
      }

      await purgeUserAccountData(firebaseUser.uid);
      await deleteUserAccount();
      return { success: true };
    } catch (error) {
      logError(error, "Delete User");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

/**
 * Upgrade existing reader to writer – update role and writer profile fields
 */
export const becomeWriterThunk = createAsyncThunk(
  "auth/becomeWriter",
  async (
    { fullName, phoneNumber, gender, authorName, bio },
    { rejectWithValue, getState }
  ) => {
    try {
      const firebaseUser = getCurrentUser();
      if (!firebaseUser) {
        return rejectWithValue("You must be logged in to become a writer.");
      }

      const authUpdates = {};
      if (fullName !== undefined && fullName !== null && String(fullName).trim()) {
        authUpdates.displayName = String(fullName).trim();
      }
      if (Object.keys(authUpdates).length > 0) {
        await updateUserProfile(authUpdates);
      }

      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const { app } = await import("./firebase");
      const upgrade = httpsCallable(getFunctions(app), "becomeWriter");
      try {
        await upgrade({ fullName, phoneNumber, gender, authorName, bio });
      } catch (error) {
        const code = String(error?.code || "");
        if (code.includes("not-found") || code.includes("unavailable")) {
          return rejectWithValue(
            "Writer upgrade is not available until the becomeWriter Cloud Function is deployed."
          );
        }
        const message = error?.message || "Could not upgrade this account.";
        return rejectWithValue(message.replace(/^FirebaseError:\s*/i, ""));
      }

      pushActivity({
        type: ACTIVITY_TYPES.USER_BECAME_WRITER,
        userId: firebaseUser.uid,
        userEmail: firebaseUser.email,
        userName: authUpdates.displayName ?? firebaseUser.displayName,
        authorName: authorName || "",
      }).catch(() => {});

      const dbUserData = await getUserData(firebaseUser.uid);
      return buildSessionUser(firebaseUser, dbUserData);
    } catch (error) {
      logError(error, "Become Writer");
      const friendlyError = formatFirebaseAuthError(error);
      return rejectWithValue(friendlyError);
    }
  }
);

